import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { date, todayIE } from "@/lib/format";
import { toast } from "sonner";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X } from "lucide-react";
import { BalanceCard, LeaveForm, LeaveList, AddButton, Badge } from "@/components/hr/HrBits";
import ProfileForm from "@/components/hr/ProfileForm";
import CertsPanel from "@/components/hr/CertsPanel";
import ApprenticePanel, { Logbook } from "@/components/hr/ApprenticePanel";

const Page = ({ title, subtitle, children, actions }) => (
  <div className="p-4 sm:p-6 lg:p-8 max-w-5xl"><PageHeader title={title} subtitle={subtitle}>{actions}</PageHeader>{children}</div>
);

export function MyLeave() {
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get(`/hr/balance/${user.id}`).then((r) => setB(r.data));
    api.get("/hr/leave", { params: { mine: true } }).then((r) => setRows(r.data));
  }, [user.id]);
  useEffect(() => { load(); }, [load]);
  const cancel = async (r) => { try { await api.post(`/hr/leave/${r.id}/cancel`); toast.success("Request cancelled"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Page title="My Leave" subtitle="Request leave and track your balance">
      <LeaveForm balance={b} onDone={load} trigger={<Button data-testid="leave-request-open" className="kiosk-btn w-full sm:w-auto mb-5 bg-primary text-primary-foreground">Request Leave</Button>} />
      <BalanceCard b={b} />
      <h3 className="font-heading font-bold uppercase text-sm mt-6 mb-2">My requests</h3>
      <LeaveList rows={rows} onCancel={cancel} />
    </Page>
  );
}

function Mentoring() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/hr/mentoring").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  return (
    <div className="mt-6" data-testid="mentoring">
      <h3 className="font-heading font-bold uppercase text-sm mb-2">Apprentices I mentor</h3>
      {rows.map((a) => (
        <div key={a.user_id} className="mb-4"><div className="text-sm font-bold">{a.name} · Phase {a.current_phase}</div>
          <Logbook uid={a.user_id} entries={a.pending_entries} canSign reload={load} /></div>
      ))}
    </div>
  );
}

export function MyProfile() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const load = useCallback(() => { api.get(`/hr/people/${user.id}`).then((r) => setP(r.data)); }, [user.id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  return (
    <Page title="My Profile" subtitle="Your details and emergency contact">
      <ProfileForm person={p} hr={false} onSaved={load} />
      {p.is_apprentice && <div className="mt-6"><h3 className="font-heading font-bold uppercase text-sm mb-2">My apprenticeship</h3><ApprenticePanel uid={user.id} hr={false} /></div>}
      <Mentoring />
    </Page>
  );
}

export function MyCerts() {
  const { user } = useAuth();
  return <Page title="My Certs" subtitle="Upload your tickets and training certs"><CertsPanel userId={user.id} hr={false} /></Page>;
}

export function DecisionButtons({ r, reload }) {
  const decide = async (action) => {
    const reason = window.prompt(action === "approve" ? "Approval note (optional)" : "Reason for rejecting (optional)") ?? null;
    if (reason === null) return;
    try { await api.post(`/hr/leave/${r.id}/decision`, { action, reason }); toast.success(action === "approve" ? "Approved" : "Rejected"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (r.status !== "pending") return null;
  return (
    <>
      <Button size="sm" onClick={() => decide("approve")} data-testid={`leave-approve-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Approve</Button>
      <Button size="sm" variant="outline" onClick={() => decide("reject")} data-testid={`leave-reject-${r.id}`} className="text-red-400"><X className="w-4 h-4 mr-1" />Reject</Button>
    </>
  );
}

function LeaveTypes() {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/hr/leave-types").then((r) => setTypes(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/hr/leave-types", { name }); setName(""); load(); } catch (e) { toast.error(apiErr(e)); } };
  const rename = async (t) => { const n = window.prompt("Rename leave type", t.name); if (n && n !== t.name) { await api.patch(`/hr/leave-types/${t.id}`, { name: n }); load(); } };
  const toggle = async (t) => { await api.patch(`/hr/leave-types/${t.id}`, { active: t.active === false }); load(); };
  return (
    <div className="max-w-xl">
      <div className="flex gap-2 mb-3"><Input placeholder="New leave type" value={name} onChange={(e) => setName(e.target.value)} data-testid="leavetype-name" /><Button onClick={add} disabled={!name} data-testid="leavetype-add">Add</Button></div>
      <div className="card-industrial divide-y divide-border">
        {types.map((t) => (
          <div key={t.id} className="p-3 flex items-center justify-between" data-testid={`leavetype-${t.id}`}>
            <div><span className={`font-semibold ${t.active === false ? "line-through text-muted-foreground" : ""}`}>{t.name}</span>{t.deducts && <span className="ml-2 text-[10px] text-primary font-mono uppercase">Deducts from balance</span>}</div>
            <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => rename(t)} data-testid={`leavetype-rename-${t.id}`}>Rename</Button>
              <Button size="sm" variant="ghost" onClick={() => toggle(t)} data-testid={`leavetype-toggle-${t.id}`}>{t.active === false ? "Enable" : "Disable"}</Button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LeaveRequests() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [who, setWho] = useState("");
  const load = useCallback(() => { api.get("/hr/leave").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); api.get("/users").then((r) => setStaff(r.data)); }, [load]);
  const pending = rows.filter((r) => r.status === "pending");
  return (
    <Page title="Leave Requests" subtitle="Approve or reject leave and add leave for staff"
      actions={<div className="flex gap-2 items-center">
        <Select value={who} onValueChange={setWho}><SelectTrigger className="w-44" data-testid="leave-add-person"><SelectValue placeholder="Add leave for…" /></SelectTrigger>
          <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
        {who && <LeaveForm userId={who} onDone={load} trigger={<AddButton label="Add" testid="leave-add-open" />} />}
      </div>}>
      <Tabs defaultValue="pending">
        <TabsList><TabsTrigger value="pending" data-testid="tab-leave-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-leave-all">All</TabsTrigger><TabsTrigger value="types" data-testid="tab-leave-types">Leave types</TabsTrigger></TabsList>
        <TabsContent value="pending"><LeaveList rows={pending} showName actions={(r) => <DecisionButtons r={r} reload={load} />} /></TabsContent>
        <TabsContent value="all"><LeaveList rows={rows} showName actions={(r) => <DecisionButtons r={r} reload={load} />} /></TabsContent>
        <TabsContent value="types"><LeaveTypes /></TabsContent>
      </Tabs>
    </Page>
  );
}

export function WhosOff() {
  const today = todayIE();
  const plus = new Date(today + "T12:00:00"); plus.setDate(plus.getDate() + 30);
  const [range, setRange] = useState([today, `${plus.getFullYear()}-${String(plus.getMonth() + 1).padStart(2, "0")}-${String(plus.getDate()).padStart(2, "0")}`]);
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/hr/whos-off", { params: { start: range[0], end: range[1] } }).then((r) => setRows(r.data)); }, [range]);
  return (
    <Page title="Who's Off" subtitle="Approved and pending leave in the selected period">
      <div className="flex gap-2 mb-4">
        <Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="w-44" data-testid="off-start" />
        <Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="w-44" data-testid="off-end" />
      </div>
      <div className="card-industrial divide-y divide-border" data-testid="whos-off-list">
        {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Nobody is off in this period.</div>}
        {rows.map((r) => (
          <div key={r.id} className="p-3 flex items-center justify-between gap-3" data-testid={`off-row-${r.id}`}>
            <div><div className="font-semibold">{r.user_name} <span className="text-xs text-muted-foreground">· {r.location}</span></div>
              <div className="text-xs text-muted-foreground">{r.type} · {date(r.start_date)} – {date(r.end_date)} · {r.days}d</div></div>
            <Badge status={r.status} />
          </div>
        ))}
      </div>
    </Page>
  );
}

export function People() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/hr/people").then((r) => setRows(r.data)); }, []);
  return (
    <Page title="People" subtitle="Staff profiles, leave, certs and apprentices">
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3 hidden sm:table-cell">Job title</th><th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-right px-4 py-3">Leave bal.</th><th className="text-left px-4 py-3">Certs</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((p) => (
              <tr key={p.id} data-testid={`person-row-${p.id}`} className="hover:bg-accent/30">
                <td className="px-4 py-3"><Link to={`/hr/people/${p.id}`} className="font-bold text-primary hover:underline" data-testid={`person-link-${p.id}`}>{p.name}</Link>
                  <div className="text-xs text-muted-foreground">{p.home_location}</div></td>
                <td className="px-4 py-3 hidden sm:table-cell">{p.profile.job_title || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{p.profile.employment_type || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-num">{p.balance}d</td>
                <td className="px-4 py-3 space-x-1">{p.certs_expired > 0 && <Badge status="expired" />}{p.certs_expiring > 0 && <Badge status="expiring" />}{!p.certs_expired && !p.certs_expiring && <span className="text-xs text-muted-foreground">OK</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

export function CertsExpiring() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/hr/certs-expiring").then((r) => setRows(r.data)); }, []);
  return (
    <Page title="Certs Expiring" subtitle="Expired certs and those expiring in the next 30 days">
      <div className="card-industrial divide-y divide-border" data-testid="certs-expiring-list">
        {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">All certs are in date.</div>}
        {rows.map((c) => (
          <div key={c.id} className="p-3 flex items-center justify-between" data-testid={`expiring-row-${c.id}`}>
            <div><Link to={`/hr/people/${c.user_id}`} className="font-bold hover:underline">{c.user_name}</Link><div className="text-xs text-muted-foreground">{c.name} · expires {date(c.expiry_date)}</div></div>
            <Badge status={c.status} />
          </div>
        ))}
      </div>
    </Page>
  );
}

export function Apprentices() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/hr/apprentices").then((r) => setRows(r.data)); }, []);
  return (
    <Page title="Apprentices" subtitle="SOLAS phases, mentors, college blocks and sign-offs. Set up a new apprentice from People → Apprentice tab.">
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm" data-testid="apprentices-table">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Apprentice</th><th className="text-left px-4 py-3">Trade</th><th className="text-left px-4 py-3">Phase</th>
              <th className="text-left px-4 py-3">Mentor</th><th className="text-left px-4 py-3">Next college block</th><th className="text-right px-4 py-3">Pending sign-offs</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No apprentices yet.</td></tr>}
            {rows.map((a) => (
              <tr key={a.user_id} data-testid={`appr-row-${a.user_id}`}>
                <td className="px-4 py-3"><Link to={`/hr/people/${a.user_id}`} className="font-bold text-primary hover:underline">{a.name}</Link></td>
                <td className="px-4 py-3">{a.trade}</td>
                <td className="px-4 py-3 font-mono-num">{a.current_phase} / 7</td>
                <td className="px-4 py-3">{a.mentor_name || "—"}</td>
                <td className="px-4 py-3">{date(a.next_college_block)}</td>
                <td className={`px-4 py-3 text-right font-mono-num ${a.pending_signoffs ? "text-amber-400 font-bold" : ""}`}>{a.pending_signoffs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
